import type { GetServerSideProps, InferGetServerSidePropsType } from 'next';
import { NextPageWithLayout } from '@/pages/_app';
import React, { useMemo } from 'react';
import Layout from '@/index-layouts';
import { AppSettings } from '@/shared/app-common';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import dynamic from 'next/dynamic';
import { getPageContentContentApi } from '@/lib/service/home';
import { getTdkApi } from '@/lib/service/tdk';
import { page3JsonLd } from '@/lib/common/jsonld';
import { hyphenatedToTitleCase, removeLastPathSegment } from '@/shared/path-helper';
import Head from 'next/head';
const GuideContent = dynamic(() => import('@/components/GuideContent'), {
  ssr: true,
});

export const getServerSideProps: GetServerSideProps = async (context) => {
  context.res.setHeader('Cache-Control', 'public, s-maxage=10, stale-while-revalidate=59');

  const { locale = 'en', resolvedUrl } = context;

  // 如果为 guide/[content] 重定向到 /guilde
  if (resolvedUrl.includes('/guide/[content]')) {
    return {
      redirect: {
        destination: `/guide`,
        permanent: false,
      },
    };
  }

  const [localeRes, tdkData, res] = await Promise.all([
    serverSideTranslations(locale, ['common']),
    getTdkApi(resolvedUrl, locale),
    getPageContentContentApi(
      `/guide/${context.params?.content}`,
      locale,
      Number(context.query.preview) || '',
      Number(context.query.preview_id) || '',
    ),
  ]);

  if (res.code != 0) {
    return {
      redirect: {
        destination: `/404`,
        permanent: false,
      },
    };
  }
  let currentURL = `${process.env.NEXT_APP_WEB_HOST}/guide/${context.params?.content}`;
  if (locale != 'en') {
    currentURL = `${process.env.NEXT_APP_WEB_HOST}/${locale}/guide/${context.params?.content}`;
  }
  const parentPath = removeLastPathSegment(resolvedUrl);

  const tempData = localeRes['_nextI18Next']['initialI18nStore'][locale]['common'] ?? {};

  const siteUrl = locale !== 'en' ? `${process.env.NEXT_APP_WEB_HOST}/${locale}` : `${process.env.NEXT_APP_WEB_HOST}`;
  const parentUrl = siteUrl + parentPath;
  const parentTitle = hyphenatedToTitleCase(parentPath);
  const pageTitle = tdkData?.data?.title ?? tempData?.[`${context.params?.content}_title`] ?? null;
  const pageDescription = tdkData?.data?.description ?? tempData?.[`${context.params?.content}_desc`] ?? null;
  const pageKeywords = tdkData?.data?.keywords ?? tempData?.[`${context.params?.content}_keys`] ?? null;

  const jsonLd = page3JsonLd(
    {
      title: pageTitle,
      desc: pageDescription,
      url: currentURL,
    },
    { title: 'Dmca Takedown', url: siteUrl + '/dmca-takedown' },
    { url: siteUrl },
  );
  return {
    props: {
      ...localeRes,
      herf: currentURL,
      locale,
      data: res.data,
      title: pageTitle,
      description: pageDescription,
      keywords: pageKeywords,
      content: context.params?.content,
      jsonLd: jsonLd,
    },
  };
};

const Home: NextPageWithLayout<InferGetServerSidePropsType<typeof getServerSideProps>> = (props: any) => {
  return useMemo(() => <GuideContent data={props.data} />, [props]);
};
Home.getLayout = function getLayout(page: React.ReactElement) {
  const title = page?.props?.title;
  const desc = page?.props?.description;
  const keywords = page?.props?.keywords;
  const jsonLd = page?.props?.jsonLd;
  return (
    <Layout
      head={
        <Head>
          <title>{title}</title>
          <meta name="title" content={title} />
          <meta name="description" content={desc} />
          <meta name="keywords" content={keywords} />

          <meta property="og:title" content={title} />
          <meta property="og:description" content={desc} />
          <meta
            property="og:image"
            content={'https://res-front.pumpsoul.com/prod-erasa/image/dmca-protect/dmca-5.png'}
          />
          <meta
            property="og:image:secure_url"
            content={'https://res-front.pumpsoul.com/prod-erasa/image/dmca-protect/dmca-5.png'}
          />
          <meta property="og:type" content="website" />
          <meta property="og:url" content={page.props?.herf} />

          {/* <meta name="twitter:title" content={title} /> */}
          <meta name="twitter:description" content={desc} />
          <meta
            name="twitter:image"
            content={'https://res-front.pumpsoul.com/prod-erasa/image/dmca-protect/dmca-5.png'}
          />
          <meta name="twitter:card" content="summary" />
          <meta name="twitter:site" content={page.props?.herf} />

          <meta
            name="viewport"
            content="width=device-width, initial-scale=1, minimum-scale=1, maximum-scale=1, shrink-to-fit=no, user-scalable=no, viewport-fit=cover"
          />
          <link
            rel="alternate"
            hrefLang="en"
            href={`${process.env.NEXT_APP_WEB_HOST}/en/guide/${page.props.content}`}
          />
          <link
            rel="alternate"
            hrefLang="zh"
            href={`${process.env.NEXT_APP_WEB_HOST}/zh/guide/${page.props.content}`}
          />
          <link
            rel="alternate"
            hrefLang="tw"
            href={`${process.env.NEXT_APP_WEB_HOST}/tw/guide/${page.props.content}`}
          />
          <link
            rel="alternate"
            hrefLang="ja"
            href={`${process.env.NEXT_APP_WEB_HOST}/ja/guide/${page.props.content}`}
          />
          <link
            rel="alternate"
            hrefLang="ko"
            href={`${process.env.NEXT_APP_WEB_HOST}/ko/guide/${page.props.content}`}
          />
          <link
            rel="alternate"
            hrefLang="pt"
            href={`${process.env.NEXT_APP_WEB_HOST}/pt/guide/${page.props.content}`}
          />
          <link
            rel="alternate"
            hrefLang="de"
            href={`${process.env.NEXT_APP_WEB_HOST}/de/guide/${page.props.content}`}
          />
          <link
            rel="alternate"
            hrefLang="es"
            href={`${process.env.NEXT_APP_WEB_HOST}/es/guide/${page.props.content}`}
          />
          <link
            rel="alternate"
            hrefLang="it"
            href={`${process.env.NEXT_APP_WEB_HOST}/it/guide/${page.props.content}`}
          />
          <link
            rel="alternate"
            hrefLang="x-default"
            href={`${process.env.NEXT_APP_WEB_HOST}/guide/${page.props.content}`}
          />

          <link rel="canonical" href={`${process.env.NEXT_APP_WEB_HOST}/guide/${page.props.content}`} />
          <link rel="icon" href={AppSettings.icoPath} />
          {jsonLd && (
            <script async type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }} />
          )}
        </Head>
      }
    >
      {page}
    </Layout>
  );
};

export default Home;
